/**
 * PricingScreen — page de choix du plan UNIQUE SaaS.
 * Adapté de cvh-app/src/app/pricing/page.tsx pour Zustand + FastAPI.
 *
 * Flux :
 *   1. L'utilisateur choisit un plan + durée (1–24 mois)
 *   2. POST /payment/initiate → URL YengaPay
 *   3. PaymentModal (iframe) → paiement
 *   4. POST /subscription → activation
 *   5. refreshPlan() → JWT mis à jour
 *   6. Navigation vers 'billing'
 */
import { useState } from 'react';
import type { JSX } from 'react';
import { Check, ArrowLeft, Sparkles } from 'lucide-react';
import { UniqueButton } from '@components/UniqueButton';
import { UniqueCard } from '@components/UniqueCard';
import { PaymentModal } from '@components/payment/PaymentModal';
import { useNavStore } from '@store/navStore';
import { useUserStore, getToken } from '@store/userStore';
import { PLAN_LABELS, PLAN_PRICES, computePrice, type PlanKey } from '@/lib/plans';
import { usePlan } from '@/hooks/usePlan';

const BACKEND_URL =
  import.meta.env.VITE_AI_BACKEND_URL ?? 'http://127.0.0.1:8787';

interface PlanCard {
  key: PlanKey;
  badge?: string;
  features: string[];
}

const PLAN_CARDS: PlanCard[] = [
  {
    key: 'free',
    features: [
      '10 tâches maximum',
      '1 briefing IA / jour',
      'Dashboard de base',
      'Communication (vue seule)',
    ],
  },
  {
    key: 'essential',
    badge: 'Populaire',
    features: [
      'Tâches illimitées',
      '5 briefings IA / jour',
      'Mode Focus + PiP',
      'Communication complète',
      'Brouillons IA (20/mois)',
      'Analytics 30 jours',
      'Export PDF',
      'SmartSave',
    ],
  },
  {
    key: 'pro',
    features: [
      'Tout Essentiel inclus',
      'Briefings & brouillons IA illimités',
      'Escalades CommHub',
      'Analytics historique complet',
      'Support prioritaire',
    ],
  },
];

export default function PricingScreen(): JSX.Element {
  const navigate = useNavStore((s) => s.navigate);
  const refreshPlan = useUserStore((s) => s.refreshPlan);
  const { plan: currentPlan } = usePlan();

  const [months, setMonths] = useState(1);
  const [selectedPlan, setSelectedPlan] = useState<'essential' | 'pro' | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [checkoutUrl, setCheckoutUrl] = useState<string | null>(null);
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [pendingRef, setPendingRef] = useState('');
  const [error, setError] = useState<string | null>(null);

  const handleSelectPlan = async (plan: 'essential' | 'pro') => {
    setSelectedPlan(plan);
    setSubmitting(true);
    setError(null);

    const token = getToken();
    const amount = computePrice(plan, months);
    const reference = `UNIQUE-${plan.toUpperCase()}-${Date.now()}`;
    setPendingRef(reference);

    try {
      const res = await fetch(`${BACKEND_URL}/payment/initiate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          amount,
          reference,
          articles: [
            {
              name:     `UNIQUE ${PLAN_LABELS[plan]} — ${months} mois`,
              quantity: months,
              price:    PLAN_PRICES[plan],
            },
          ],
        }),
      });
      const data = await res.json();
      if (!data.success) {
        setError(data.detail ?? 'Erreur lors de l\'initialisation du paiement.');
        return;
      }
      const d = data.data;
      const url =
        d.checkoutPageUrlWithPaymentToken ??
        d.checkout_url ??
        d.url ??
        d.payment_url ??
        d.paymentLink ??
        (d.token ? `https://checkout.yengapay.com/checkout/${d.token}` : null);

      if (!url) {
        setError('Impossible d\'obtenir le lien de paiement YengaPay.');
        return;
      }
      setCheckoutUrl(url);
      setPaymentOpen(true);
    } catch {
      setError('Erreur réseau. Vérifiez votre connexion et réessayez.');
    } finally {
      setSubmitting(false);
    }
  };

  const handlePaymentSuccess = async () => {
    setPaymentOpen(false);
    setCheckoutUrl(null);
    if (!selectedPlan) return;

    const token = getToken();
    try {
      await fetch(`${BACKEND_URL}/subscription`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ plan: selectedPlan, months, reference: pendingRef }),
      });
      await refreshPlan();
      navigate('billing');
    } catch {
      setError('Paiement effectué mais erreur d\'activation. Contactez le support.');
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-6 py-10">
      {/* En-tête */}
      <div className="flex items-center gap-3 mb-8">
        <UniqueButton variant="ghost" size="sm" leftIcon={<ArrowLeft size={16} />} onClick={() => navigate('dashboard')}>
          Retour
        </UniqueButton>
        <div>
          <h1 className="!border-none !pb-0 !text-3xl">Choisissez votre plan</h1>
          <p className="text-sm mt-1" style={{ color: 'var(--color-text-medium)' }}>
            Payez en FCFA · Mobile Money · Visa · Mastercard
          </p>
        </div>
      </div>

      {/* Sélecteur de durée */}
      <UniqueCard padding="lg" className="mb-8 max-w-md">
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm font-semibold">Durée d'abonnement</span>
          <span
            className="text-xl font-extrabold"
            style={{ color: 'var(--color-primary-dark)' }}
          >
            {months} mois
          </span>
        </div>
        <input
          type="range"
          min={1}
          max={24}
          step={1}
          value={months}
          onChange={(e) => setMonths(Number(e.target.value))}
          className="w-full cursor-pointer"
          style={{ accentColor: 'var(--color-primary-dark)' }}
        />
        <div
          className="flex justify-between text-[11px] mt-1"
          style={{ color: 'var(--color-text-medium)' }}
        >
          <span>1 mois</span>
          <span>6 mois</span>
          <span>12 mois</span>
          <span>24 mois</span>
        </div>
        {months >= 6 && (
          <p
            className="mt-2 text-xs font-semibold"
            style={{ color: 'var(--color-important)' }}
          >
            ✓ Remise fidélité :{' '}
            {months >= 24 ? '25%' : months >= 12 ? '15%' : months >= 6 ? '10%' : '5%'} appliquée
          </p>
        )}
      </UniqueCard>

      {error && (
        <div
          role="alert"
          className="mb-5 rounded-card px-4 py-3 text-sm"
          style={{
            background: 'rgba(239,83,80,0.08)',
            border: '1px solid rgba(239,83,80,0.35)',
            color: 'var(--color-urgent)',
          }}
        >
          {error}
        </div>
      )}

      {/* Cartes plans */}
      <div className="grid md:grid-cols-3 gap-5 items-start">
        {PLAN_CARDS.map((card) => {
          const isPaid = card.key !== 'free';
          const isCurrent = card.key === currentPlan;
          const totalPrice = computePrice(card.key, months);

          return (
            <div
              key={card.key}
              className="unique-card flex flex-col"
              style={{
                padding: 24,
                ...(card.badge
                  ? {
                      border: '2px solid var(--color-primary-dark)',
                      boxShadow: 'var(--shadow-hover)',
                      transform: 'translateY(-4px)',
                    }
                  : {}),
              }}
            >
              {card.badge && (
                <div className="mb-3">
                  <span
                    className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wide px-2 py-0.5 rounded-full"
                    style={{
                      background: 'var(--color-primary)',
                      color: 'var(--color-text-dark)',
                    }}
                  >
                    <Sparkles size={10} />
                    {card.badge}
                  </span>
                </div>
              )}

              {/* Prix */}
              <div className="mb-5">
                <h2
                  className="text-base font-bold mb-1"
                  style={{ color: 'var(--color-text-dark)' }}
                >
                  {PLAN_LABELS[card.key]}
                </h2>
                <div
                  className="text-3xl font-extrabold"
                  style={{ color: 'var(--color-primary-dark)' }}
                >
                  {isPaid ? `${totalPrice.toLocaleString('fr-FR')} FCFA` : 'Gratuit'}
                </div>
                {isPaid && months > 1 && (
                  <p
                    className="text-xs mt-1"
                    style={{ color: 'var(--color-text-medium)' }}
                  >
                    {PLAN_PRICES[card.key].toLocaleString('fr-FR')} FCFA/mois × {months} mois
                  </p>
                )}
              </div>

              {/* Features */}
              <ul className="flex-1 space-y-2 mb-6">
                {card.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm">
                    <Check
                      size={14}
                      className="mt-0.5 shrink-0"
                      style={{ color: 'var(--color-important)' }}
                    />
                    <span style={{ color: 'var(--color-text-dark)' }}>{f}</span>
                  </li>
                ))}
              </ul>

              {/* CTA */}
              {isCurrent ? (
                <div
                  className="text-center text-sm font-semibold py-2 rounded-lg"
                  style={{
                    background: 'var(--color-primary-glow)',
                    color: 'var(--color-text-medium)',
                  }}
                >
                  Plan actuel
                </div>
              ) : isPaid ? (
                <UniqueButton
                  fullWidth
                  isLoading={submitting && selectedPlan === card.key}
                  onClick={() => handleSelectPlan(card.key as 'essential' | 'pro')}
                >
                  Choisir {PLAN_LABELS[card.key]}
                </UniqueButton>
              ) : (
                <div
                  className="text-center text-sm py-2 rounded-lg"
                  style={{ background: 'var(--color-primary-glow)', color: 'var(--color-text-medium)' }}
                >
                  Inclus
                </div>
              )}
            </div>
          );
        })}
      </div>

      <p
        className="text-center text-xs mt-8"
        style={{ color: 'var(--color-text-medium)' }}
      >
        Paiement sécurisé via YengaPay · Mobile Money (Orange, Moov, Wave) · Visa · Mastercard
      </p>

      <PaymentModal
        open={paymentOpen}
        checkoutUrl={checkoutUrl}
        onSuccess={handlePaymentSuccess}
        onClose={() => {
          setPaymentOpen(false);
          setCheckoutUrl(null);
        }}
      />
    </div>
  );
}
